import { Link, useParams } from 'react-router-dom';
import { FaArchive, FaBriefcase, FaClipboard, FaDesktop, FaEnvelope, FaInbox, FaListAlt } from 'react-icons/fa';
import styled from 'styled-components';
import GoHubTopNav from '../sharedComponents/GoHubTopNav';
import useIsMobileView from '../hooks/useIsMobileView';
import QuerySf from './Shared/QuerySf';
import Orders from './Orders/Orders';
import Products from './Products/Products';
import Inventory from './Inventory/Inventory';
import Dashboard from './Dashboard/Dashboard';
import WRO from './WRO/WRO';
import Opportunities from './Opportunities/Opportunities';
import Email from './Email/Email';
import OrderTrackingModals from './OrderTrackingModals';
import useOrderTrackingModals from './Shared/useOrderTrackingModals';

import { appRootPath } from '../utils';
const drawerWidth = 240;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 100%;
  padding-top: 39px; /* room for the fixed hub top nav */
  box-sizing: border-box;
`

/*
 colors:
  button-blue #0280c6
  background-blue: #016092?

  text-blue: #0280c6
  background-powder-blue: #f0faff

  text-gray: #707070
*/
const Aside = styled.div`
  width: calc(${drawerWidth}px - 40px); 
  padding: 20px;
  border-right: solid 1px #C2C9D1;
  background-color: white;
  color: #707070;
`

const MainWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`

// Rendered inside the dark hub top bar (GoHubTopNav appLogo slot)
const HubBarTitle = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
`

const MainContent = styled.div`
  flex: 1;
  width: calc(100% - 40px);
  padding: 20px;
  background: #f5f5f5;
`

// Fills the hub top nav's mobile drawer (240px wide, white)
const MobileDrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  color: #707070;
`

const DrawerItem = styled(Link)`
  display: flex;
  padding: 20px;
  border-radius: 8px;
  text-decoration: none;
  
  &:link, &:visited, &:active {
    color: #707070;
  }
  &:hover {
    color: #0280c6;
  }

  & .nav-icon {
    padding-right: 10px;
  }
`

const navLinks = [
  { name: 'Dashboard', url: '', icon: <FaDesktop /> },
  { name: 'Opportunities', url: 'opportunities', icon: <FaBriefcase /> },
  { name: 'Orders', url: 'orders', icon: <FaClipboard /> },
  { name: 'Products', url: 'products', icon: <FaListAlt /> },
  { name: 'Inventory', url: 'inventory', icon: <FaArchive /> },
  { name: 'WROs', url: 'wros', icon: <FaInbox /> },
  { name: 'Email', url: 'email', icon: <FaEnvelope />, isHidden: true }
]

const getSelectedNav = (tab) => navLinks.find(n => n.url === tab) || navLinks[0]

// Nav links shared by the desktop aside and the hub top nav's mobile drawer
const ShippingNavLinks = ({ onNavigate }) => {
  const { tab } = useParams();
  const selectedNav = getSelectedNav(tab)

  return (
    <>
      {navLinks.filter(n => !n.isHidden).map(nav => 
        <DrawerItem 
          key={nav.name}
          to={`${appRootPath}/shipping/${nav.url}`}
          style={{...(nav.name === selectedNav.name ? { backgroundColor: '#f0faff', color: '#0280c6' } : {})}}
          onClick={onNavigate}
        >
          <span className='nav-icon'>{nav.icon}</span> {nav.name}
        </DrawerItem>
      )}
    </>
  )
}

// HubTopNav renders this inside its hamburger drawer on mobile
const ShippingMobileNav = ({ setIsDrawerOpen }) => (
  <MobileDrawerContent>
    <ShippingNavLinks onNavigate={() => setIsDrawerOpen(false)} />
  </MobileDrawerContent>
)

export default function OrderTrackingHome () {
  const { tab } = useParams();

  const isMobileView = useIsMobileView()

  const { shipModals, modalState } = useOrderTrackingModals()

  const navComponents = {
    '': <Dashboard shipModals={shipModals} />,
    opportunities: <Opportunities shipModals={shipModals} />,
    orders: <Orders shipModals={shipModals} />,
    products: <Products />,
    inventory: <Inventory />,
    wros: <WRO />,
    email: <Email />
  }

  const selectedNav = getSelectedNav(tab)

  const main = (
    <div>{navComponents[selectedNav.url]}</div>
  )

  return (
    <>
    <GoHubTopNav appLogo={<HubBarTitle>{'Shipping Portal'}</HubBarTitle>} MobileDrawerNav={ShippingMobileNav} />
    <Layout>
      <MainWrapper>
        {
          !isMobileView &&
          <Aside>
            <ShippingNavLinks />
          </Aside>
        }

        <MainContent>
          {main}
          <QuerySf />
          <OrderTrackingModals shipModals={shipModals} modalState={modalState} />
        </MainContent>
      </MainWrapper>
    </Layout>
    </>
  );
}
